import { useEffect, useRef } from 'react';
import { useVisualizerStore } from '@/stores/useVisualizerStore';
import { isSafeImageUrl } from '@/utils/urlSecurity';

interface CanvasViewerProps {
  isCompareMode?: boolean;
}

export function CanvasViewer({ isCompareMode = false }: CanvasViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const beforeImgRef = useRef<HTMLImageElement | null>(null);
  const afterImgRef = useRef<HTMLImageElement | null>(null);

  const uploadedImage = useVisualizerStore((s) => s.uploadedImage);
  const previewResultUrl = useVisualizerStore((s) => s.previewResultUrl);
  const beforeAfterRatio = useVisualizerStore((s) => s.beforeAfterRatio);

  const hasPreviewResult = Boolean(previewResultUrl);

  // Block casual save vectors (right-click "Save Image As" / "Open Image in New
  // Tab", drag-to-tab, and iOS long-press) on the preview viewer. Scoped to this
  // container only so right-click works normally elsewhere. Does not (and is not
  // meant to) stop DevTools/network or screenshots.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const blockContextMenu = (e: Event) => e.preventDefault();
    const blockDrag = (e: Event) => e.preventDefault();
    container.addEventListener('contextmenu', blockContextMenu);
    container.addEventListener('dragstart', blockDrag);
    return () => {
      container.removeEventListener('contextmenu', blockContextMenu);
      container.removeEventListener('dragstart', blockDrag);
    };
  }, []);

  // Apply non-grabbable styling to an imperatively created image element.
  const protectImage = (img: HTMLImageElement) => {
    img.draggable = false;
    img.style.userSelect = 'none';
    img.style.setProperty('-webkit-user-select', 'none');
    img.style.setProperty('-webkit-user-drag', 'none');
    img.style.setProperty('-webkit-touch-callout', 'none');
    img.style.pointerEvents = 'none';
  };

  // Create/update image elements when results arrive
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !uploadedImage) return;

    if (!hasPreviewResult) return;

    if (isCompareMode) {
      let beforeImg = beforeImgRef.current;
      if (!beforeImg || !container.contains(beforeImg)) {
        beforeImg = document.createElement('img');
        beforeImg.className = 'absolute inset-0 h-full w-full object-contain';
        beforeImg.alt = 'Original';
        protectImage(beforeImg);
        container.appendChild(beforeImg);
        beforeImgRef.current = beforeImg;
      }
      if (isSafeImageUrl(uploadedImage.sourceUrl) && beforeImg.src !== uploadedImage.sourceUrl) {
        beforeImg.src = uploadedImage.sourceUrl;
      }
    } else {
      if (beforeImgRef.current) {
        beforeImgRef.current.remove();
        beforeImgRef.current = null;
      }
    }

    // Create/update after image with crossfade
    let afterImg = afterImgRef.current;
    if (!afterImg || !container.contains(afterImg)) {
      afterImg = document.createElement('img');
      afterImg.className = 'absolute inset-0 h-full w-full object-contain';
      afterImg.style.transition = 'opacity 0.45s ease';
      afterImg.style.opacity = '0';
      afterImg.alt = 'CounterVision preview';
      protectImage(afterImg);
      container.appendChild(afterImg);
      afterImgRef.current = afterImg;
    }

    if (isSafeImageUrl(previewResultUrl) && afterImg.src !== previewResultUrl!) {
      // Fade out → swap src → fade in
      afterImg.style.opacity = '0';
      const newSrc = previewResultUrl!;
      const onLoad = () => {
        afterImg!.style.opacity = '1';
        afterImg!.removeEventListener('load', onLoad);
      };
      afterImg.addEventListener('load', onLoad);
      afterImg.src = newSrc;
    }

    if (isCompareMode) {
      afterImg.style.clipPath = `inset(0 0 0 ${beforeAfterRatio * 100}%)`;
    } else {
      afterImg.style.clipPath = 'none';
    }

    return () => {
      beforeImgRef.current?.remove();
      afterImgRef.current?.remove();
      beforeImgRef.current = null;
      afterImgRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedImage, previewResultUrl, hasPreviewResult, isCompareMode]);

  // Update before/after clip when ratio changes (only in compare mode)
  useEffect(() => {
    const afterImg = afterImgRef.current;
    if (afterImg && hasPreviewResult && isCompareMode) {
      afterImg.style.clipPath = `inset(0 0 0 ${beforeAfterRatio * 100}%)`;
    }
  }, [beforeAfterRatio, hasPreviewResult, isCompareMode]);

  if (!uploadedImage) {
    return (
      <div className="flex h-full items-center justify-center text-slate-400 text-sm">
        No image loaded
      </div>
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center p-2 sm:p-3">
      <div
        ref={containerRef}
        onContextMenu={(e) => e.preventDefault()}
        className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-lg select-none"
      >
        {!hasPreviewResult && (
          <img
            src={isSafeImageUrl(uploadedImage.sourceUrl) ? uploadedImage.sourceUrl : ''}
            alt="Your bathroom"
            draggable={false}
            className="max-h-full max-w-full object-contain select-none [pointer-events:none] [-webkit-touch-callout:none] [-webkit-user-drag:none]"
          />
        )}
      </div>
    </div>
  );
}
